/**
 * Deep link handling for native Capacitor apps
 * Listens for app URL open events and routes within the SPA
 */

import { isNativeApp } from './platform';

/**
 * Initialize deep link listener for native app
 * Should be called once when app mounts
 */
export function initializeDeepLinks(onUrl: (url: string) => void): () => void {
  if (!isNativeApp()) {
    // Not in native app, no deep links to handle
    return () => {};
  }

  let unsubscribe: (() => void) | null = null;

  // Use Function constructor to prevent Vite from statically analyzing the import
  const dynamicImport = new Function('specifier', 'return import(specifier)') as (
    specifier: string
  ) => Promise<unknown>;

  dynamicImport('@capacitor/app')
    .then((appModule: unknown) => {
      const app = appModule as { App?: { addListener: (e: string, cb: (ev: { url: string }) => void) => Promise<{ remove: () => void }> } };
      if (!app?.App) {
        console.warn('[DeepLinks] Capacitor App plugin not available');
        return;
      }

      const listener = app.App.addListener('appUrlOpen', (event: { url: string }) => {
        const url = event.url;
        if (url) {
          onUrl(url);
        }
      });

      unsubscribe = () => {
        listener.then((l: { remove: () => void }) => l.remove()).catch(() => {});
      };
    })
    .catch((error) => {
      // Silently handle missing package - this is expected in web builds
      if (import.meta.env.DEV) {
        console.warn('[DeepLinks] Capacitor App plugin not installed (this is normal for web builds):', error);
      }
    });

  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

/**
 * Parse deep link URL and extract route path
 * Handles custom URL schemes like postpartner://chat/123
 */
export function parseDeepLinkUrl(url: string): { path: string; params: Record<string, string> } | null {
  try {
    const urlObj = new URL(url);
    
    // Extract path from URL
    // For custom schemes: postpartner://chat/123 -> /chat/123
    // For universal links: https://postpartner.app/chat/123 -> /chat/123
    let path = urlObj.pathname;
    
    // If it's a custom scheme, the pathname might be empty, check hash
    if (!path && urlObj.hash) {
      path = urlObj.hash.replace('#', '');
    }
    
    // Extract query parameters
    const params: Record<string, string> = {};
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });
    
    return { path, params };
  } catch (error) {
    console.warn('[DeepLinks] Failed to parse URL:', url, error);
    return null;
  }
}
